import React from 'react';
import { motion } from 'framer-motion';
import { BotIcon, WorkflowIcon, TrendingUpIcon, MessageSquareIcon, CheckIcon } from 'lucide-react';
import { CTASection } from '../components/CTASection';
export function AIAutomation() {
  const CapabilitySection = ({
    icon: Icon,
    title,
    body,
    capabilities,
    delay = 0
  }: {
    icon: any;
    title: string;
    body: string;
    capabilities: string[];
    delay?: number;
  }) => <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.6,
    delay
  }} className="mb-16" data-id="element-93">
      <div className="flex items-start gap-4 mb-6" data-id="element-94">
        <div className="w-12 h-12 bg-surface rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-95">
          <Icon className="w-6 h-6 text-primary" data-id="element-96" />
        </div>
        <div className="flex-1" data-id="element-97">
          <h2 className="text-3xl font-bold text-ink mb-4" data-id="element-98">{title}</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-99">{body}</p>

          <div className="bg-surface rounded-xl p-6" data-id="element-100">
            <h3 className="text-lg font-bold text-ink mb-4" data-id="element-101">Capabilities:</h3>
            <ul className="space-y-3" data-id="element-102">
              {capabilities.map((capability, index) => <li key={index} className="flex items-start" data-id="element-103">
                  <CheckIcon className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-104" />
                  <span className="text-gray-700" data-id="element-105">{capability}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </motion.div>;
  return <div className="min-h-screen w-full bg-white" data-id="element-106">
      {/* Hero */}
      <section className="bg-gradient-to-b from-surface to-white py-20" data-id="element-107">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-108">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center" data-id="element-109">
            <h1 className="text-4xl md:text-5xl font-bold text-ink mb-6" data-id="element-110">
              AI & Automation Solutions
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-111">
              Magnus Software brings enterprise-grade AI capabilities to your
              business. From intelligent assistants to automated workflows, we
              implement practical AI solutions that reduce manual work, improve
              decision-making, and scale your operations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20" data-id="element-112">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-113">
          <CapabilitySection icon={BotIcon} title="Intelligent Assistants That Learn Your Business" body="Deploy AI assistants that understand your domain, answer customer questions, and automate routine tasks. Our conversational AI solutions integrate with your existing systems and improve over time through machine learning." capabilities={['Custom-trained AI assistants for your industry', 'Natural language understanding and generation', 'Context-aware responses and recommendations', 'Integration with CRM, knowledge bases, and databases', 'Multi-channel deployment (web, mobile, Slack, Teams)']} delay={0} data-id="element-114" />

          <CapabilitySection icon={WorkflowIcon} title="Automate Repetitive Work" body="Connect your tools and eliminate manual data entry with intelligent automation. We build custom workflows that trigger actions across your software stack, saving time and reducing errors." capabilities={['Custom automation workflows', 'API integrations between platforms', 'Data synchronization and transformation', 'Scheduled tasks and event-driven triggers', 'Error handling and notification systems']} delay={0.1} data-id="element-115" />

          <CapabilitySection icon={TrendingUpIcon} title="Optimize with AI-Driven Analytics" body="Leverage machine learning to improve your website's search rankings and user experience. Our AI tools analyze performance data, identify opportunities, and provide actionable recommendations." capabilities={['AI content optimization and keyword analysis', 'Automated performance monitoring', 'Predictive analytics for traffic and conversions', 'Competitor analysis and benchmarking', 'Personalized content recommendations']} delay={0.2} data-id="element-116" />

          <CapabilitySection icon={MessageSquareIcon} title="Never Miss a Conversation" body="Engage website visitors around the clock with AI-powered chat that answers questions, qualifies leads, and escalates to your team when needed. Provide instant support without expanding headcount." capabilities={['Conversational AI trained on your content', 'Lead qualification and routing', 'Multi-language support', 'Seamless handoff to human agents', 'Analytics and conversation insights']} delay={0.3} data-id="element-117" />
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-surface" data-id="element-118">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-119">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-120">
            <h2 className="text-3xl md:text-4xl font-bold text-ink mb-8 text-center" data-id="element-121">
              Practical AI Applications
            </h2>

            <div className="bg-white rounded-xl p-8 md:p-12" data-id="element-122">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-123">
                <li className="flex items-start" data-id="element-124">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-125" />
                  <span className="text-gray-700" data-id="element-126">
                    Customer support automation reducing ticket volume by 60%+
                  </span>
                </li>
                <li className="flex items-start" data-id="element-127">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-128" />
                  <span className="text-gray-700" data-id="element-129">
                    Sales lead qualification and intelligent routing
                  </span>
                </li>
                <li className="flex items-start" data-id="element-130">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-131" />
                  <span className="text-gray-700" data-id="element-132">
                    Document processing and data extraction
                  </span>
                </li>
                <li className="flex items-start" data-id="element-133">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-134" />
                  <span className="text-gray-700" data-id="element-135">
                    Predictive maintenance for equipment and systems
                  </span>
                </li>
                <li className="flex items-start" data-id="element-136">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-137" />
                  <span className="text-gray-700" data-id="element-138">
                    Inventory optimization and demand forecasting
                  </span>
                </li>
                <li className="flex items-start" data-id="element-139">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-140" />
                  <span className="text-gray-700" data-id="element-141">
                    Personalized product recommendations
                  </span>
                </li>
                <li className="flex items-start" data-id="element-142">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-143" />
                  <span className="text-gray-700" data-id="element-144">
                    Automated content generation and summarization
                  </span>
                </li>
                <li className="flex items-start" data-id="element-145">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-146" />
                  <span className="text-gray-700" data-id="element-147">
                    Sentiment analysis for customer feedback
                  </span>
                </li>
                <li className="flex items-start" data-id="element-148">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-149" />
                  <span className="text-gray-700" data-id="element-150">
                    Fraud detection and anomaly identification
                  </span>
                </li>
                <li className="flex items-start" data-id="element-151">
                  <CheckIcon className="w-6 h-6 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-152" />
                  <span className="text-gray-700" data-id="element-153">
                    Process optimization through pattern recognition
                  </span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white" data-id="element-154">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-155">
          <CTASection headline="Ready to Leverage AI?" body="Let's discuss how AI and automation can transform your business operations." buttonText="Discuss Your AI Project" buttonLink="/contact" variant="dark" data-id="element-156" />
        </div>
      </section>
    </div>;
}